import {
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@musa.tech/design-system";
import type { GranularityType } from "@/hooks/useEvolutionChartData";
import {
  formatHeatmapPeriodLabel,
  periodsSpanMultipleYears,
  type HeatmapData,
} from "./volumeHeatmapAggregation";
import {
  cascadeDelayMs,
  getVolumeIntensity,
  VOLUME_INTENSITY_CLASS,
} from "./volumeHeatmapScale";

interface VolumeHeatmapGridProps {
  data: HeatmapData;
  granularity: GranularityType;
}

const formatVolume = (liters: number) =>
  `${Math.trunc(liters).toLocaleString("pt-BR")} L`;

/**
 * Grade do mapa de calor (Figma node 233:2583): rótulo da unidade à esquerda,
 * uma célula por período.
 *
 * A coluna de rótulos é `sticky` porque a granularidade diária num intervalo
 * longo gera dezenas de colunas — sem isso, rolar para a direita esconderia
 * de qual unidade é cada linha.
 */
export const VolumeHeatmapGrid = ({ data, granularity }: VolumeHeatmapGridProps) => {
  if (!data.rows.length) {
    return (
      <div className="flex min-h-[200px] flex-1 items-center justify-center">
        <Text variant="body" size="small" className="text-neutral-500">
          Nenhuma unidade com volume no período
        </Text>
      </div>
    );
  }

  // Sem o ano, um intervalo que cruza a virada mostraria duas colunas "Jan".
  const includeYear = periodsSpanMultipleYears(data.periods);

  return (
    <TooltipProvider>
      <div className="min-w-0 flex-1 overflow-x-auto">
        <div className="inline-flex min-w-full flex-col gap-1.5">
          {/* Cabeçalho de períodos — alinhado com as células pela mesma largura.
              O espaçador precisa de `self-stretch`: numa linha `items-center`
              ele não estica sozinho, ficaria com altura 0 e os rótulos
              passariam por baixo dele ao rolar na horizontal. */}
          <div className="flex items-center gap-1.5">
            <div className="sticky left-0 z-10 w-[140px] shrink-0 self-stretch bg-white" />
            {data.periods.map((period) => (
              <div
                key={period}
                className="flex w-[52px] shrink-0 items-center justify-center"
              >
                <Text className="whitespace-nowrap text-xs text-neutral-500">
                  {formatHeatmapPeriodLabel(period, granularity, includeYear)}
                </Text>
              </div>
            ))}
          </div>

          {data.rows.map((row) => (
            <div key={row.establishmentId} className="flex items-center gap-1.5">
              {/* `self-stretch` pelo mesmo motivo: o fundo branco tem que cobrir
                  os 30px da linha (altura das células), não só a altura do texto. */}
              <div className="sticky left-0 z-10 flex w-[140px] shrink-0 items-center justify-end self-stretch bg-white pr-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className="block cursor-help truncate whitespace-nowrap text-xs font-medium text-neutral-500"
                    >
                      {row.establishmentName}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs leading-relaxed">
                    {row.establishmentName} — {formatVolume(row.totalVolume)} no período
                  </TooltipContent>
                </Tooltip>
              </div>

              {row.cells.map((value, index) => (
                <Tooltip key={data.periods[index]}>
                  <TooltipTrigger asChild>
                    <div
                      tabIndex={0}
                      // O atraso cresce com a coluna, produzindo a entrada em
                      // cascata da esquerda pra direita. Teto em CASCADE_MAX_DELAY_MS
                      // para que uma grade longa não demore a terminar.
                      style={{ animationDelay: `${cascadeDelayMs(index)}ms` }}
                      className={cn(
                        "h-[30px] w-[52px] shrink-0 cursor-help rounded",
                        "animate-in fade-in zoom-in-95 fill-mode-both duration-300",
                        "motion-reduce:animate-none",
                        VOLUME_INTENSITY_CLASS[
                          getVolumeIntensity(value, data.maxCellVolume)
                        ],
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs leading-relaxed">
                    {row.establishmentName} ·{" "}
                    {formatHeatmapPeriodLabel(
                      data.periods[index],
                      granularity,
                      includeYear,
                    )}{" "}
                    · {formatVolume(value)}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
};
